import { ManageSupplierStatus } from 'src/types/manage-suppliers/ManageSupplier';
import {
  initialSupplierFormValues,
  ManageSupplierEditContext,
  SupplierEditMode,
  SupplierFieldAccess,
  SupplierFormFieldKey,
  SupplierFormValues,
} from 'src/types/manage-suppliers/SupplierForm';
import { toSupplierFieldValue } from './supplierFormValues';

const allFields = (editable: boolean): SupplierFieldAccess =>
  Object.fromEntries(
    (Object.keys(initialSupplierFormValues) as SupplierFormFieldKey[]).map((key) => [
      key,
      editable,
    ])
  ) as SupplierFieldAccess;

export const viewOnlySupplierFieldAccess = allFields(false);

const noFields = viewOnlySupplierFieldAccess;

/** Editable fields for online (hosted/punchout) supplier overrides. */
export const onlineSupplierOverrideFieldAccess: SupplierFieldAccess = {
  ...noFields,
  externalSupplierName: true,
  apId: true,
};

/**
 * Status used to compute edit-field access during an open edit session.
 * Form selection drives lock/unlock: Active unlocks fields; Inactive/Archived
 * locks to status-only. Dirty field edits are still persisted on save via
 * resolveOfflineUpsertValues (legacy parity).
 */
export const resolveStatusForEditAccess = (
  persistedStatus: ManageSupplierStatus,
  formStatus?: ManageSupplierStatus
): ManageSupplierStatus => formStatus ?? persistedStatus;

export type SupplierEditAccessInput = Pick<
  ManageSupplierEditContext,
  'supplierType' | 'status' | 'isSameCompany'
>;

interface GetSupplierEditAccessParams {
  supplier: SupplierEditAccessInput;
  canManageSuppliers: boolean;
  canManageTransmissionSettings: boolean;
  hasEditPermission: boolean;
}

export interface SupplierEditAccessResult {
  mode: SupplierEditMode;
  fields: SupplierFieldAccess;
  canSave: boolean;
}

export const getSupplierEditAccess = ({
  supplier,
  canManageSuppliers,
  canManageTransmissionSettings,
  hasEditPermission,
}: GetSupplierEditAccessParams): SupplierEditAccessResult => {
  if (!hasEditPermission) {
    return { mode: 'view', fields: noFields, canSave: false };
  }

  const isOnlineSupplier = supplier.supplierType !== 'offline';

  if (isOnlineSupplier) {
    if (!canManageSuppliers) {
      return { mode: 'view', fields: noFields, canSave: false };
    }

    if (supplier.status === 'inactive' || supplier.status === 'archived') {
      return { mode: 'view', fields: noFields, canSave: false };
    }

    return {
      mode: 'online',
      fields: onlineSupplierOverrideFieldAccess,
      canSave: true,
    };
  }

  // Managed-by (shared) inactive/archived: no edits at all (including overrides and status).
  if (
    !supplier.isSameCompany &&
    (supplier.status === 'inactive' || supplier.status === 'archived')
  ) {
    return { mode: 'view', fields: noFields, canSave: false };
  }

  // Managed-by (shared) active suppliers: limited overrides; status is never editable.
  // Must run before the statusOnly branch so Inactive/Archived does not unlock status radios.
  if (!supplier.isSameCompany && canManageSuppliers) {
    return {
      mode: 'crossCompany',
      fields: {
        ...noFields,
        externalSupplierName: true,
        apId: true,
        accountNumber: true,
      },
      canSave: true,
    };
  }

  if (supplier.status === 'inactive' || supplier.status === 'archived') {
    if (!canManageSuppliers) {
      return { mode: 'view', fields: noFields, canSave: false };
    }

    return {
      mode: 'statusOnly',
      fields: {
        ...noFields,
        status: true,
      },
      canSave: true,
    };
  }

  if (canManageTransmissionSettings && !canManageSuppliers && supplier.isSameCompany) {
    return {
      mode: 'transmissionOnly',
      fields: {
        ...noFields,
        transmissionType: true,
        orderEmail: true,
      },
      canSave: true,
    };
  }

  if (canManageSuppliers && supplier.isSameCompany) {
    return {
      mode: 'full',
      fields: allFields(true),
      canSave: true,
    };
  }

  return { mode: 'view', fields: noFields, canSave: false };
};

export const isSupplierOverrideActive = (
  values: Pick<SupplierFormValues, 'externalSupplierName' | 'apId'>,
  originalExternalSupplierName: string | null | undefined,
  originalApId: string | null | undefined
): boolean => {
  const externalSupplierName = toSupplierFieldValue(values.externalSupplierName).trim();
  const apId = toSupplierFieldValue(values.apId).trim();
  const originalExternalName = toSupplierFieldValue(originalExternalSupplierName).trim();
  const originalAp = toSupplierFieldValue(originalApId).trim();

  if (!originalExternalName && !originalAp) {
    return false;
  }

  if (!externalSupplierName && !apId) {
    return false;
  }

  return externalSupplierName !== originalExternalName || apId !== originalAp;
};

export const getEffectiveFieldAccess = (
  fieldAccess: SupplierFieldAccess,
  _values: Pick<SupplierFormValues, 'transmissionType'>
): SupplierFieldAccess => fieldAccess;
